import React, { useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Upload,
  Download,
  FileSpreadsheet,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Bulk-upload widget — used on SIMs, Devices, Customers, Vehicles.
 *
 * Two-step UX:
 *   1. User picks file → POST {uploadUrl}?dry_run=1 → server runs full
 *      validation (lookup, dup-checks, date parsing, intra-file IMEI conflicts)
 *      WITHOUT persisting and returns {inserted, errors, total}.
 *   2. We render a preview card. If 0 errors → green "Confirm" CTA.
 *      If errors → orange warning + per-row error list. User can either
 *      Confirm anyway (valid rows still import, errored rows are skipped) or
 *      Cancel + fix the file.
 *
 * Props: templateUrl, uploadUrl, templateName, onDone, label, testIdPrefix.
 */
export default function BulkUploadButton({
  templateUrl,
  uploadUrl,
  templateName,
  onDone,
  label = "Bulk Upload",
  testIdPrefix = "bulk",
}) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { inserted, errors, total, file }
  const [result, setResult] = useState(null);    // after confirm

  const reset = () => {
    setPreview(null);
    setResult(null);
    setBusy(false);
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get(templateUrl, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = templateName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Download failed");
    }
  };

  // Step 1: validation-only preview
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setResult(null);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`${uploadUrl}?dry_run=1`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview({ ...r.data, file });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not read the file");
    } finally {
      setBusy(false);
    }
  };

  // Step 2: confirm — actually persist
  const confirmUpload = async () => {
    if (!preview?.file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", preview.file);
      const r = await api.post(uploadUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      setPreview(null);
      if (r.data.inserted > 0) toast.success(`${r.data.inserted} record(s) imported`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} row(s) skipped`);
      if (onDone) onDone();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const previewErrorCount = preview?.errors?.length || 0;
  const previewValidCount = preview?.inserted || 0;
  const previewTotal = preview?.total || 0;
  const previewAllClean = preview && previewErrorCount === 0 && previewValidCount > 0;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="rounded-none"
        onClick={() => { reset(); setOpen(true); }}
        data-testid={`${testIdPrefix}-open`}
      >
        <Upload className="w-4 h-4 mr-2" /> {label}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent
          className="rounded-none border vsm-border max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid={`${testIdPrefix}-dialog`}
        >
          <DialogHeader>
            <div className="vsm-overline">Bulk Upload</div>
            <DialogTitle className="text-2xl font-bold tracking-tight">
              Upload from Excel
            </DialogTitle>
            <DialogDescription className="vsm-muted text-sm">
              Download the sample template, fill in the rows, then upload. We&apos;ll
              validate the file before importing — nothing is saved until you confirm.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Step 0: Download sample */}
            <div className="border vsm-border p-4 flex items-center gap-3">
              <FileSpreadsheet className="w-8 h-8 vsm-primary-text shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Sample Template</div>
                <div className="text-xs vsm-muted">Dropdowns pre-populated from your current data.</div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="rounded-none"
                onClick={downloadTemplate}
                data-testid={`${testIdPrefix}-template`}
              >
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
            </div>

            {/* Step 1: pick file */}
            {!preview && !result && (
              <div>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={onFile}
                  data-testid={`${testIdPrefix}-file-input`}
                />
                <Button
                  type="button"
                  className="rounded-none vsm-primary-bg hover:opacity-90 w-full"
                  disabled={busy}
                  onClick={() => inputRef.current?.click()}
                  data-testid={`${testIdPrefix}-pick-file`}
                >
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  {busy ? "Validating…" : "Choose .xlsx file to validate"}
                </Button>
                <div className="vsm-muted text-xs mt-2">
                  The file is checked for missing fields, unknown lookup values, duplicate IDs and
                  conflicting IMEIs before anything is saved.
                </div>
              </div>
            )}

            {/* Step 2: preview */}
            {preview && (
              <div
                className={
                  "border p-4 space-y-3 " +
                  (previewAllClean
                    ? "border-green-600 bg-green-50 dark:bg-green-950/30"
                    : "border-amber-600 bg-amber-50 dark:bg-amber-950/30")
                }
                data-testid={`${testIdPrefix}-preview`}
              >
                <div className="flex items-center gap-2 font-semibold">
                  {previewAllClean ? (
                    <CheckCircle2 className="w-5 h-5 text-green-700" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-700" />
                  )}
                  <div>
                    Validation result —
                    <span className="ml-1.5 text-green-700">{previewValidCount} valid</span>
                    {previewErrorCount > 0 && (
                      <span className="ml-2 text-red-700">{previewErrorCount} error(s)</span>
                    )}
                    <span className="ml-2 vsm-muted font-normal">of {previewTotal} row(s)</span>
                  </div>
                </div>

                {previewErrorCount > 0 && (
                  <div className="border vsm-border bg-white dark:bg-zinc-900 max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 vsm-surface border-b vsm-border">
                        <tr className="text-left">
                          <th className="px-3 py-2 vsm-overline w-20">Row</th>
                          <th className="px-3 py-2 vsm-overline">Issue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.errors.map((e) => (
                          <tr
                            key={`row-${e.row}-${e.error}`}
                            className="border-t vsm-border"
                            data-testid={`${testIdPrefix}-preview-row-${e.row}`}
                          >
                            <td className="px-3 py-1.5 font-mono">{e.row}</td>
                            <td className="px-3 py-1.5 text-red-700">{e.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-none"
                    onClick={reset}
                    data-testid={`${testIdPrefix}-cancel-preview`}
                  >
                    Cancel &amp; pick a different file
                  </Button>
                  <Button
                    type="button"
                    className="rounded-none vsm-primary-bg hover:opacity-90 flex-1"
                    disabled={busy || previewValidCount === 0}
                    onClick={confirmUpload}
                    data-testid={`${testIdPrefix}-confirm`}
                  >
                    {busy
                      ? "Importing…"
                      : previewErrorCount === 0
                      ? `Import ${previewValidCount} row(s)`
                      : `Import ${previewValidCount} valid row(s) (skip ${previewErrorCount})`}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: post-import summary */}
            {result && (
              <div
                className="border vsm-border p-4 text-sm space-y-1"
                data-testid={`${testIdPrefix}-result`}
              >
                <div className="font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-700" />
                  Imported <strong>{result.inserted}</strong> of <strong>{result.total}</strong> row(s).
                </div>
                {result.errors?.length > 0 && (
                  <details className="text-xs vsm-muted">
                    <summary className="cursor-pointer mt-1">
                      {result.errors.length} skipped row(s) — click to expand
                    </summary>
                    <ul className="list-disc pl-5 mt-2 space-y-0.5">
                      {result.errors.map((e) => (
                        <li key={`row-${e.row}-${e.error}`}>
                          Row {e.row}: {e.error}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="rounded-none"
              onClick={() => setOpen(false)}
              data-testid={`${testIdPrefix}-close`}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
